import qs from 'qs';

import type { SsrAppApiDirectOptions } from '@/data/appApi';
import apiRequest from '@/utils/apiRequest';
import { RequestUserData } from '@/utils/apiRequest/types';
import { calmLogger } from '@/utils/calmLogger';
import { isPromoOffer } from '@/utils/promoOffer/isPromoOffer';

// Timeout for this single fetch. The init-path callers await it inside
// `Promise.allSettled` alongside every other init fetch, so leaving it at the
// transport's 15s default would let one slow/hanging promo-offers call stall the
// entire page's SSR response.
const PROMO_OFFER_TIMEOUT_MS = 1500;

export interface PromoOffer {
	id: string;
	period: string;
	starts_at: string;
	ends_at: string;
	surface: string;
	destination_url: string;
	coupon: string | null;
	title: string;
	subtitle: string | null;
	variant?: string | null;
	cta_label: string;
	dismiss_label: string | null;
	analytics: {
		pack_class: string;
		flavor: string;
	};
}

interface PromoOfferResponse {
	offer: PromoOffer | null;
}

function toOffer(response: PromoOfferResponse | undefined, surface: string): PromoOffer | null {
	const candidate = response?.offer ?? null;
	if (candidate === null) return null;
	if (isPromoOffer(candidate)) return candidate;
	// Query params (e.g. evaluation_date, utm_*) can carry personal data, so only the
	// pathname is logged. A `?` only ever appears once here, at the boundary buildSurface
	// serializes via URLSearchParams.
	calmLogger.warn('Malformed promo offer payload, showing no banner', { surface: surface.split('?')[0] });
	return null;
}

export async function getPromoOffer(
	surface: string | undefined,
	userData: RequestUserData,
	opts?: SsrAppApiDirectOptions,
	evaluationDate?: string,
): Promise<PromoOffer | null> {
	if (!surface) return null;

	const endpoint = `promo-offers?${qs.stringify({
		surface,
		...(evaluationDate ? { evaluation_date: evaluationDate } : {}),
	})}`;

	const fetchOffer = async (): Promise<PromoOffer | null> => {
		if (typeof window === 'undefined' && opts?.ctx) {
			// SSR app-api-direct: `/promo-offers` accepts an optional Bearer, so a logged-out
			// visitor still gets an offer. `requiresAuth: true` resolves that Bearer when a
			// session exists — `getHeaders` only sets `Authorization` when it finds a token,
			// so an anonymous request still omits the header instead of failing.
			const { appApiServerRequest } = await import('@/data/appApi');
			const { data } = await appApiServerRequest<PromoOfferResponse>({
				endpoint,
				ctx: opts.ctx,
				xLanguage: opts.xLanguage,
				requiresAuth: true,
				timeout: PROMO_OFFER_TIMEOUT_MS,
			});
			return toOffer(data, surface);
		}

		const result = await apiRequest<PromoOfferResponse>({
			endpoint,
			method: 'GET',
			userData,
			timeout: PROMO_OFFER_TIMEOUT_MS,
		});

		return toOffer(result?.data, surface);
	};

	// PROMO_OFFER_TIMEOUT_MS above only bounds the axios call itself, which starts ticking
	// after getClassicTokenSet()/header resolution — a hang there wouldn't otherwise be capped.
	// Racing the whole fetch (including that resolution) against this deadline caps the total
	// wait SSR pays for this call, regardless of where the time goes.
	let deadlineTimer!: ReturnType<typeof setTimeout>;
	const deadline = new Promise<never>((_resolve, reject) => {
		deadlineTimer = setTimeout(
			() => reject(new Error('Timed out waiting for promo offer')),
			PROMO_OFFER_TIMEOUT_MS,
		);
	});

	try {
		return await Promise.race([fetchOffer(), deadline]);
	} catch (err) {
		// apiRequest rejects with the raw AxiosResponse-like object, whose `config`/`url`
		// carry this request's full URL (including the surface query) — logging it verbatim
		// would put that in the warn payload, so only status and a message are extracted.
		const rejection = err as { status?: number; data?: { error?: { message?: string } }; message?: string };
		calmLogger.warn('Error retrieving promo offer via GET /promo-offers', {
			status: rejection?.status,
			message: rejection?.data?.error?.message ?? (err instanceof Error ? err.message : String(err)),
		});
		return null;
	} finally {
		clearTimeout(deadlineTimer);
	}
}
